// ABOUTME: Inline chat card rendered when the assistant has just persisted a
// ContentDraft via create_content. Shows title + truncated body + platform
// pills + Edit/Schedule/Delete actions so the SMB owner can act on the draft
// without leaving the chat. Mirrors the style of SchedulingCard /
// InsufficientCreditsCard. Edit is a stub for this PR.

import { useState } from 'react'
import { Pencil, Calendar, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getPlatformBrand } from '@/lib/platforms'
import { cn } from '@/lib/utils'
import { CHAT_CARD } from '@/components/chat/chatCard'

const BODY_TRUNCATE_LIMIT = 200

interface DraftPreviewProps {
  draftId: string
  title: string
  body: string
  platforms: string[]
  onEdit: (draftId: string) => void
  onSchedule: (draftId: string) => void
  onDelete: (draftId: string) => void
}

export function DraftPreview({
  draftId,
  title,
  body,
  platforms,
  onEdit,
  onSchedule,
  onDelete,
}: DraftPreviewProps) {
  const [expanded, setExpanded] = useState(false)
  const trimmed = (body ?? '').trim()
  const isLong = trimmed.length > BODY_TRUNCATE_LIMIT
  const displayBody =
    !expanded && isLong ? `${trimmed.slice(0, BODY_TRUNCATE_LIMIT).trimEnd()}…` : trimmed

  return (
    <div data-testid={`draft-preview-${draftId}`} className={cn(CHAT_CARD, 'my-2 space-y-2')}>
      {/* Title */}
      {title && <h3 className="text-sm leading-snug font-medium">{title}</h3>}

      {/* Body */}
      {trimmed && (
        <p className="text-foreground/90 text-xs leading-relaxed whitespace-pre-wrap">
          {displayBody}
          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-primary ml-1 underline underline-offset-2"
              data-testid="draft-preview-toggle"
            >
              {expanded ? 'show less' : 'show more'}
            </button>
          )}
        </p>
      )}

      {/* Platform pills */}
      {platforms.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-0.5">
          {platforms.map((p) => {
            const brand = getPlatformBrand(p)
            const Icon = brand.icon
            return (
              <span
                key={p}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.625rem] font-medium',
                  brand.bg,
                  brand.fg
                )}
              >
                <Icon className="size-2.5" strokeWidth={2.5} />
                {brand.label}
              </span>
            )
          })}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-1 pt-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(draftId)}
          className="h-7 gap-1 text-xs"
          data-testid="draft-preview-edit"
        >
          <Pencil className="size-3" />
          Edit
        </Button>
        <Button
          size="sm"
          onClick={() => onSchedule(draftId)}
          className="h-7 gap-1 text-xs"
          data-testid="draft-preview-schedule"
        >
          <Calendar className="size-3" />
          Schedule
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(draftId)}
          className="text-destructive hover:text-destructive h-7 gap-1 text-xs"
          data-testid="draft-preview-delete"
        >
          <Trash2 className="size-3" />
          Delete
        </Button>
      </div>
    </div>
  )
}
